// WYVIO UI 1.0 — Espace Client V3
// Route canonique : /client/welcome  ·  Alias recette : /client/espace-v3, /client/espace-v3/:id
// Preview agence : /app/espace-client-v3/:id (mode="preview")
// mode="client" utilise les endpoints /api/client/* (aucun endpoint agence).

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Check, Clock, ArrowRight, FileText, Download, MessageCircle,
  Send, Phone, Sparkles,
} from "lucide-react";

import api from "@/lib/api";
import { LogoGlyph, LogoWordmark } from "@/branding/Logo";
import { SilhouetteVehicle } from "@/components/wyvio-ui/signature/SilhouetteVehicle";

/* ═════════════════════════ Utils ═════════════════════════ */
const guessSilhouette = (name = "") => {
  const v = name.toLowerCase();
  if (/gt3|coupe|coupé|911|m2|m4|corvette|amg gt|f-type|cayman/.test(v)) return "coupe";
  if (/suv|q3|q5|q7|q8|x1|x3|x5|x7|macan|cayenne|glc|gle|gls|3008|5008|2008|rav|kodiaq/.test(v)) return "suv";
  if (/rs6|break|estate|touring|allroad|shooting|avant/.test(v)) return "break";
  return "berline";
};

/* Mapping des étapes métier VÉHICULE → 6 étapes client compréhensibles.
   `paiement` est EXCLU volontairement : c'est une action transversale
   qui ne doit JAMAIS écraser la position d'avancement véhicule. */
const CLIENT_STAGES = [
  { key: "reservation", label: "Réservation" },
  { key: "dossier",     label: "Dossier" },
  { key: "controles",   label: "Contrôles" },
  { key: "preparation", label: "Préparation" },
  { key: "pret",        label: "Prêt" },
  { key: "livraison",   label: "Livraison" },
];

const VEHICLE_STAGE_OF = {
  reservation:      0,
  acompte:          1,
  admin:            1,
  check:            2,
  check_controle:   2,
  ct:               2,
  mecanique:        2,
  esthetique:       3,
  pret:             4,
  livraison:        5,
  admin_traitement: 5,
  cpi:              5,
  immat:            5,
  cloture:          5,
  // 'paiement' est volontairement absent → transversal
};

const _COMPLETED = new Set(["completed", "termine", "done"]);

/**
 * Détermine l'étape client à partir des steps véhicule uniquement.
 * `paiement` est ignoré : il n'affecte JAMAIS la position d'avancement.
 * On retourne l'index de la PREMIÈRE stage qui contient au moins une step
 * non terminée. Toutes stages terminées → 6 (livré).
 */
const clientStageIndex = (dossier) => {
  if (dossier?.is_finished) return CLIENT_STAGES.length;
  const steps = dossier?.steps || [];
  const stageHasStep    = new Array(CLIENT_STAGES.length).fill(false);
  const stageCompleted  = new Array(CLIENT_STAGES.length).fill(true);
  for (const s of steps) {
    if (s.key === "paiement") continue;   // transversal, jamais bloquant pour la timeline
    const idx = VEHICLE_STAGE_OF[s.key];
    if (idx == null) continue;
    stageHasStep[idx] = true;
    const st = s.state || s.status;
    if (!_COMPLETED.has(st)) stageCompleted[idx] = false;
  }
  for (let i = 0; i < CLIENT_STAGES.length; i++) {
    if (stageHasStep[i] && !stageCompleted[i]) return i;
  }
  // Toutes les stages véhicule sont completed
  return CLIENT_STAGES.length;
};

const humanTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  const yesterday = d.toDateString() === y.toDateString();
  const hhmm = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  if (sameDay)   return `Aujourd'hui · ${hhmm}`;
  if (yesterday) return `Hier · ${hhmm}`;
  const days = Math.round((now.getTime() - d.getTime()) / 86400000);
  if (days > 0 && days < 7) return `Il y a ${days} j`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
};

/* ═════════════════════════ Header compact ════════════════ */
const ClientHeader = ({ agency }) => (
  <header
    className="sticky top-0 z-30"
    style={{ background: "rgba(250,250,247,0.85)", backdropFilter: "saturate(140%) blur(14px)", WebkitBackdropFilter: "saturate(140%) blur(14px)", borderBottom: "1px solid rgba(0,0,0,0.05)" }}
    data-testid="client-v3-header"
  >
    <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-4 lg:px-10">
      <div className="flex items-center gap-2">
        <LogoGlyph size={22} />
        <LogoWordmark size={16} color="#0A0A0A" />
      </div>
      <div className="truncate text-[11.5px] font-medium text-black/50">{agency?.name || "wyvio"}</div>
    </div>
  </header>
);

/* ═════════════════════════ Hero DESKTOP immersif ═════════ */
const ClientHeroDesktop = ({ dossier }) => {
  const silhouette = guessSilhouette(dossier?.vehicle_name);
  const stageIdx = clientStageIndex(dossier);
  const raw = dossier?.progress || 0;
  const s = dossier?.current_status?.color;
  const statusMap = {
    green:  "#C8FF2F", blue: "#3B82F6", orange: "#F59E0B",
    red:    "#EF4444", gray: "#94A3B8", purple: "#8B5CF6",
  };
  const dot = statusMap[s] || statusMap.gray;

  return (
    <section
      className="relative w-full overflow-hidden bg-[#0A0A0A]"
      style={{ height: "min(560px, 62vh)" }}
      data-testid="client-v3-hero-desktop"
    >
      {/* Photo */}
      {dossier?.vehicle_image ? (
        <img
          src={dossier.vehicle_image}
          alt={dossier.vehicle_name}
          className="absolute inset-0 h-full w-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <SilhouetteVehicle type={silhouette} variant="dark" className="w-[46%]" />
        </div>
      )}

      {/* Gradient bas — lisibilité du contenu overlay */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 45%, rgba(0,0,0,0.55) 78%, rgba(0,0,0,0.75) 100%)",
        }}
      />
      {/* Gradient gauche — bloc info plus lisible */}
      <div
        aria-hidden
        className="absolute inset-y-0 left-0 w-[55%]"
        style={{
          background:
            "linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 60%, rgba(0,0,0,0) 100%)",
        }}
      />

      {/* Contenu overlay */}
      <div className="relative mx-auto flex h-full max-w-[1440px] flex-col justify-end px-10 pb-10">
        <div className="grid grid-cols-1 items-end gap-8 md:grid-cols-[minmax(0,1fr)_360px]">
          {/* Colonne gauche : identité véhicule */}
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full bg-black/45 px-2.5 py-1 backdrop-blur-md"
                 style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)" }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: dot, boxShadow: `0 0 6px ${dot}66` }} />
              <span className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/95">
                {dossier?.current_status?.label || "En cours"}
              </span>
            </div>

            <div className="mt-5 text-[10.5px] uppercase tracking-[0.18em] text-white/50">
              Votre véhicule
            </div>
            <h1 className="mt-2 text-[52px] font-semibold leading-[1] tracking-[-0.025em] text-white">
              {dossier?.vehicle_name || "Véhicule"}
            </h1>
            <div className="mt-3 flex items-center gap-2.5 text-[13.5px] text-white/70">
              <span>{dossier?.order_number || "—"}</span>
              <span className="h-1 w-1 rounded-full bg-white/40" />
              <span className="truncate">
                {dossier?.client?.name || dossier?.client_name || "Vous"}
              </span>
            </div>
          </div>

          {/* Colonne droite : progression */}
          <div className="min-w-0 rounded-2xl bg-black/40 p-5 backdrop-blur-md"
               style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)" }}>
            <div className="mb-2 flex items-baseline justify-between">
              <span className="text-[10px] uppercase tracking-[0.16em] text-white/55">Avancement</span>
              <span className="text-[13px] font-medium text-white">{raw}%</span>
            </div>
            <div className="relative h-[6px] w-full overflow-hidden rounded-full bg-white/[0.10]">
              <div className="absolute inset-y-0 left-0 rounded-full"
                   style={{ width: `${raw}%`, background: "linear-gradient(90deg, #EAEAEA 0%, #FFFFFF 100%)" }} />
              {raw > 4 && raw < 100 && (
                <div className="absolute top-0 h-full w-[6px] rounded-full"
                     style={{ left: `calc(${raw}% - 6px)`, background: "#C8FF2F", boxShadow: "0 0 6px rgba(200,255,47,0.7)" }} />
              )}
            </div>
            <div className="mt-3 text-[11.5px] text-white/70">
              Étape <span className="font-semibold text-white">{Math.min(stageIdx + 1, CLIENT_STAGES.length)} sur {CLIENT_STAGES.length}</span> · {CLIENT_STAGES[Math.min(stageIdx, CLIENT_STAGES.length - 1)]?.label}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ═════════════════════════ Hero client ═══════════════════ */
const ClientHero = ({ dossier }) => {
  const silhouette = guessSilhouette(dossier?.vehicle_name);
  const s = dossier?.current_status?.color;
  const statusMap = {
    green:  "#C8FF2F",
    blue:   "#3B82F6",
    orange: "#F59E0B",
    red:    "#EF4444",
    gray:   "#94A3B8",
    purple: "#8B5CF6",
  };
  const dot = statusMap[s] || statusMap.gray;

  return (
    <section className="px-5 pt-6" data-testid="client-v3-hero">
      {/* Photo véhicule */}
      <div
        className="relative aspect-[16/10] w-full overflow-hidden rounded-3xl bg-[#0A0A0A]"
        style={{ boxShadow: "0 10px 40px -20px rgba(0,0,0,0.35)" }}
      >
        {dossier?.vehicle_image ? (
          <img
            src={dossier.vehicle_image}
            alt={dossier.vehicle_name}
            className="absolute inset-0 h-full w-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <SilhouetteVehicle type={silhouette} variant="dark" className="w-[70%]" />
          </div>
        )}
        {/* Dégradé bas pour lisibilité éventuelle */}
        <div className="absolute inset-x-0 bottom-0 h-1/3"
             style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 100%)" }} />

        {/* Statut en overlay coin haut-gauche */}
        <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-black/45 px-2.5 py-1 backdrop-blur-md"
             style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)" }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: dot, boxShadow: `0 0 6px ${dot}66` }} />
          <span className="text-[10.5px] font-medium uppercase tracking-[0.12em] text-white/95">
            {dossier?.current_status?.label || "En cours"}
          </span>
        </div>
      </div>

      {/* Titre + méta */}
      <div className="mt-6">
        <div className="text-[10.5px] uppercase tracking-[0.16em] text-black/40">
          Votre véhicule
        </div>
        <h1 className="mt-2 text-[30px] font-semibold leading-[1.05] tracking-[-0.02em] text-black">
          {dossier?.vehicle_name || "Véhicule"}
        </h1>
        <div className="mt-2 flex items-center gap-2 text-[13px] text-black/55">
          <span>{dossier?.order_number || "—"}</span>
          <span className="h-1 w-1 rounded-full bg-black/25" />
          <span className="truncate">
            {dossier?.client?.name || dossier?.client_name || "Vous"}
          </span>
        </div>
      </div>
    </section>
  );
};

/* ═════════════════════════ Progression 5 étapes ═════════ */
const ClientProgress = ({ dossier }) => {
  const stageIdx = clientStageIndex(dossier);
  const raw = dossier?.progress || 0;
  return (
    <section className="px-5 pt-8" data-testid="client-v3-progress">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-[10.5px] uppercase tracking-[0.16em] text-black/45">Avancement</span>
        <span className="text-[13px] font-medium text-black">{raw}%</span>
      </div>
      <div className="relative h-[6px] w-full overflow-hidden rounded-full bg-black/[0.06]">
        <div className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-500"
             style={{ width: `${raw}%`, background: "linear-gradient(90deg, #1F1F1F 0%, #2A2A2A 100%)" }} />
        {raw > 4 && raw < 100 && (
          <div className="absolute top-0 h-full w-[6px] rounded-full"
               style={{ left: `calc(${raw}% - 6px)`, background: "#C8FF2F", boxShadow: "0 0 4px rgba(200,255,47,0.55)" }} />
        )}
      </div>
      <div className="mt-2.5 text-[12px] text-black/50">
        Étape <span className="font-medium text-black">{Math.min(stageIdx + 1, CLIENT_STAGES.length)} sur {CLIENT_STAGES.length}</span> · {CLIENT_STAGES[Math.min(stageIdx, CLIENT_STAGES.length - 1)]?.label}
      </div>
    </section>
  );
};

/* ═════════════════════════ Next action (client) ═══════════
   Priorise l'action PAIEMENT (transversale) sur le next_action workflow.
   - status=confirmed             → chip discret "Paiement confirmé"
   - status=declared              → état rassurant "Paiement déclaré · agence vérifie"
   - will_settle_in_agency & !confirmed → "Règlement prévu en agence"
   - reminder_due (pending)       → "Paiement à finaliser"
   Sinon → next_action workflow (client) ou "aucune action requise".
*/
const ClientNextAction = ({ dossier, onSettleInAgency, settleLoading }) => {
  const ps = dossier?.payment_summary || {};
  const next = dossier?.workflow_summary?.next_action;

  // Cas 1 — Paiement confirmé (chip discret)
  if (ps.status === "confirmed") {
    return (
      <section className="px-5 pt-8" data-testid="client-v3-next-action">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#F1FBCB] px-3 py-1.5"
             style={{ boxShadow: "inset 0 0 0 1px rgba(200,255,47,0.35)" }}>
          <Check size={13} className="text-[#4A6300]" strokeWidth={2.5} />
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-[#4A6300]">
            Paiement confirmé
          </span>
        </div>
      </section>
    );
  }

  // Cas 2 — Paiement déclaré (client a déclaré, agence vérifie).
  // État rassurant, aucune action ni rappel côté client.
  if (ps.status === "declared") {
    return (
      <section className="px-5 pt-8" data-testid="client-v3-next-action">
        <div className="rounded-2xl bg-white/70 p-5"
             style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }}>
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-black/[0.06]">
              <Clock size={15} className="text-black/60" strokeWidth={1.7} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] uppercase tracking-[0.16em] text-black/50">
                Paiement déclaré
              </div>
              <p className="mt-1 text-[13.5px] leading-relaxed text-black">
                Votre règlement a bien été déclaré. Votre agence vérifie sa réception.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Cas 3 — Rappel de paiement actif (uniquement pending + controls OK + !settle_in_agency)
  if (ps.reminder_due) {
    return (
      <section className="px-5 pt-8" data-testid="client-v3-next-action">
        <div className="relative overflow-hidden rounded-3xl bg-black p-6 text-white">
          <div aria-hidden className="pointer-events-none absolute right-[-60px] top-[-60px] h-[180px] w-[180px] rounded-full"
               style={{ background: "radial-gradient(circle, rgba(200,255,47,0.35) 0%, rgba(200,255,47,0) 70%)" }} />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-[#C8FF2F] px-2.5 py-0.5">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-black">
                Paiement à finaliser
              </span>
            </div>
            <h2 className="mt-4 text-[24px] font-semibold leading-tight tracking-[-0.015em]">
              Le solde doit être réglé avant la livraison
            </h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-white/75">
              Les contrôles de votre véhicule sont terminés. Vous pouvez également
              prévoir votre règlement directement en agence lors de la livraison.
            </p>
            <div className="mt-6 grid grid-cols-1 gap-2.5">
              <button
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C8FF2F] px-5 py-3.5 text-[14px] font-medium text-black transition-transform active:scale-[0.98]"
                style={{ boxShadow: "0 6px 20px -10px rgba(200,255,47,0.6)" }}
                data-testid="client-v3-primary-cta"
              >
                Voir les instructions de paiement <ArrowRight size={15} />
              </button>
              <button
                onClick={() => onSettleInAgency?.(true)}
                disabled={settleLoading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white/[0.06] px-5 py-3 text-[13.5px] font-medium text-white/85 transition-colors active:bg-white/[0.10]"
                style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.10)" }}
                data-testid="client-v3-settle-in-agency"
              >
                {settleLoading ? "…" : "Je réglerai en agence"}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Cas 4 — Intention "règlement en agence" active
  if (ps.will_settle_in_agency) {
    return (
      <section className="px-5 pt-8" data-testid="client-v3-next-action">
        <div className="rounded-2xl bg-white/70 p-5"
             style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }}>
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-black/[0.06]">
              <Sparkles size={16} className="text-black/60" strokeWidth={1.7} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] uppercase tracking-[0.16em] text-black/50">
                Règlement prévu en agence
              </div>
              <p className="mt-1 text-[13.5px] leading-relaxed text-black">
                Votre règlement sera finalisé avec l&apos;agence lors de la livraison.
              </p>
              <button
                onClick={() => onSettleInAgency?.(false)}
                disabled={settleLoading}
                className="mt-3 text-[12px] font-medium text-black/55 underline underline-offset-2 transition-colors active:text-black"
                data-testid="client-v3-cancel-in-agency"
              >
                {settleLoading ? "…" : "Je préfère finalement payer à distance"}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Cas 5 — Rien à faire côté client
  if (!next) return null;
  const label = next?.label || next?.key || "Aucune action";
  return (
    <section className="px-5 pt-8" data-testid="client-v3-next-action">
      <div className="flex items-start gap-3 rounded-2xl bg-white/70 p-5"
           style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)" }}>
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-black/[0.06]">
          <Sparkles size={16} className="text-black/60" strokeWidth={1.7} />
        </div>
        <div className="min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.16em] text-black/45">Aucune action requise</div>
          <p className="mt-1 text-[14px] leading-relaxed text-black">
            Nous nous occupons de tout. Prochaine étape&nbsp;:
            <span className="font-medium"> {label}</span>.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ═════════════════════════ Timeline verticale 5 étapes ═══ */
const ClientTimeline = ({ dossier }) => {
  const stageIdx = clientStageIndex(dossier);
  return (
    <section className="px-5 pt-10" data-testid="client-v3-timeline">
      <div className="mb-4 text-[10.5px] uppercase tracking-[0.2em] text-black">Parcours</div>
      <ol className="relative border-l border-black/[0.08] pl-6">
        {CLIENT_STAGES.map((st, i) => {
          const isDone = i < stageIdx;
          const isActive = i === stageIdx;
          return (
            <li key={st.key} className="relative pb-6 last:pb-0">
              {/* Point */}
              <span className="absolute left-[-31px] top-0 flex h-6 w-6 items-center justify-center rounded-full"
                    style={{
                      background: isDone ? "#0C0C0C" : isActive ? "#C8FF2F" : "#FFFFFF",
                      boxShadow: `inset 0 0 0 1.5px ${isDone ? "#0C0C0C" : isActive ? "#C8FF2F" : "rgba(0,0,0,0.14)"}`,
                    }}>
                {isDone && <Check size={12} className="text-white" strokeWidth={3} />}
                {isActive && <span className="h-1.5 w-1.5 rounded-full bg-black" />}
              </span>

              {/* Contenu */}
              <div className={[
                "text-[15px] leading-tight",
                isActive ? "font-semibold text-black" : isDone ? "text-black/80" : "text-black/40",
              ].join(" ")}>
                {st.label}
              </div>
              {isActive && (
                <div className="mt-1 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.14em] text-black/55">
                  <Clock size={10} /> En cours
                </div>
              )}
              {isDone && (
                <div className="mt-1 text-[11.5px] text-black/45">Terminée</div>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
};

/* ═════════════════════════ Documents (client) ═════════════ */
const ClientDocuments = ({ dossier }) => {
  const docs = (dossier?.documents || []).filter((d) => d.client_visible !== false);
  const list = docs.length > 0 ? docs : [];

  const humanName = (d) => d.name || d.display_name || d.type_label || d.type || d.filename || "Pièce";
  const STATE = {
    signed:    { txt: "Signé",       color: "#4A6300", bg: "#F1FBCB" },
    validated: { txt: "Validé",      color: "#4A6300", bg: "#F1FBCB" },
    available: { txt: "Disponible",  color: "#4A6300", bg: "#F1FBCB" },
    pending:   { txt: "À vérifier",  color: "#5B21B6", bg: "#ECE4FE" },
    upcoming:  { txt: "En attente",  color: "#374151", bg: "#EEF0F2" },
  };

  return (
    <section className="px-5 pt-10" data-testid="client-v3-documents">
      <div className="mb-4 flex items-baseline justify-between">
        <div className="text-[10.5px] uppercase tracking-[0.2em] text-black">Vos documents</div>
        <div className="text-[11.5px] text-black/45">{list.length}</div>
      </div>

      {list.length === 0 ? (
        <div className="rounded-2xl bg-white/60 p-6 text-center text-[13px] text-black/45"
             style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)" }}>
          Aucun document disponible pour le moment.
        </div>
      ) : (
        <ul className="space-y-2">
          {list.map((d) => {
            const st = STATE[d.status] || STATE.upcoming;
            const isReady = d.status === "signed" || d.status === "available" || d.status === "validated";
            return (
              <li key={d.id}
                  className="flex items-center gap-3 rounded-2xl bg-white/70 p-4 transition-colors active:bg-white"
                  style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)" }}>
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-black/[0.05]">
                  <FileText size={17} className="text-black/60" strokeWidth={1.6} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14px] font-medium text-black">{humanName(d)}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-[11.5px] text-black/45">
                    <span>{d.date_label || "—"}</span>
                    {d.size && <><span>·</span><span>{d.size}</span></>}
                  </div>
                </div>
                {isReady ? (
                  <button
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-black text-white transition-transform active:scale-[0.94]"
                    aria-label={`Télécharger ${humanName(d)}`}
                    data-testid={`client-v3-doc-download-${d.id}`}
                  >
                    <Download size={15} strokeWidth={1.9} />
                  </button>
                ) : (
                  <span className="rounded-full px-2.5 py-1 text-[9.5px] font-semibold uppercase tracking-[0.08em]"
                        style={{ background: st.bg, color: st.color }}>
                    {st.txt}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

/* ═════════════════════════ Messages (client) ═════════════ */
const ClientMessages = ({ dossier, onSent, mode = "client" }) => {
  const msgs = dossier?.messages || [];
  const list = msgs.length > 0 ? msgs.slice(-4) : [];

  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);

  const send = async () => {
    const value = text.trim();
    if (!value || sending) return;
    setSending(true);
    setErr(null);
    try {
      if (mode === "client") {
        await api.clientSendMessage(value);
      } else if (dossier?.id) {
        // Preview agence : réutilise l'endpoint agence pour tester l'aller-retour.
        await api.sendAgencyMessage(dossier.id, value);
      }
      setText("");
      if (onSent) await onSent();
    } catch (e) {
      setErr(e?.data?.detail || e?.message || "Envoi impossible");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <section className="px-5 pt-10" data-testid="client-v3-messages">
      <div className="mb-4 flex items-center gap-2">
        <MessageCircle size={13} className="text-black/45" strokeWidth={2} />
        <div className="text-[10.5px] uppercase tracking-[0.2em] text-black">Messages</div>
      </div>

      <div className="rounded-3xl bg-white/70 p-4"
           style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)" }}>
        {list.length === 0 ? (
          <div className="py-6 text-center text-[13px] text-black/45">
            Aucun message pour le moment.
          </div>
        ) : (
          <ul className="space-y-3">
            {list.map((m) => {
              const isAgency = m.from_side === "agency";
              const when = humanTime(m.created_at);
              return (
                <li key={m.id} className={["flex flex-col gap-1", isAgency ? "items-start" : "items-end"].join(" ")}>
                  <div className="flex items-baseline gap-2 text-[9.5px] uppercase tracking-[0.14em] text-black/40">
                    <span>{isAgency ? (m.author || "Agence") : "Vous"}</span>
                    {when && <><span>·</span><span>{when}</span></>}
                  </div>
                  <div className={[
                    "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed",
                    isAgency ? "text-white" : "bg-[#F1FBCB] text-black/85",
                  ].join(" ")}
                       style={isAgency
                         ? { background: "#171717" }
                         : { boxShadow: "inset 0 0 0 1px rgba(200,255,47,0.35)" }}>
                    {m.text}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Composer tactile — branché sur POST /api/client/messages */}
        <div className="mt-4 flex items-center gap-2 rounded-full bg-white px-3 py-2"
             style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }}>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            disabled={sending}
            placeholder="Écrire à l'agence…"
            className="flex-1 bg-transparent text-[14px] text-black placeholder:text-black/35 focus:outline-none disabled:opacity-60"
            data-testid="client-v3-message-input"
          />
          <button
            type="button"
            onClick={send}
            disabled={sending || !text.trim()}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#C8FF2F] text-black transition-transform active:scale-[0.94] disabled:opacity-50 disabled:active:scale-100"
            aria-label="Envoyer"
            data-testid="client-v3-send"
          >
            <Send size={14} strokeWidth={2} />
          </button>
        </div>
        {err && (
          <div className="mt-2 text-[12px] text-red-600" data-testid="client-v3-message-error">
            {err}
          </div>
        )}
      </div>
    </section>
  );
};

/* ═════════════════════════ Footer contact ═══════════════ */
const ClientFooter = ({ agency }) => (
  <section className="mt-10 px-5 pb-14" data-testid="client-v3-footer">
    <div className="rounded-3xl bg-white/60 p-6 text-center"
         style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)" }}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-black/45">Un doute&nbsp;?</div>
      <div className="mt-1.5 text-[15px] font-medium text-black">{agency?.name || "Votre agence"}</div>
      <a href={`tel:${agency?.phone || ""}`}
         className="mt-4 inline-flex items-center gap-2 rounded-full bg-black px-5 py-2.5 text-[13px] font-medium text-white transition-transform active:scale-[0.98]">
        <Phone size={13} strokeWidth={1.9} /> Appeler l&apos;agence
      </a>
    </div>
    <div className="mt-6 flex items-center justify-center gap-1.5 opacity-70">
      <LogoGlyph size={14} />
      <span className="text-[10.5px] uppercase tracking-[0.18em] text-black/50">Propulsé par wyvio</span>
    </div>
  </section>
);

/* ═════════════════════════ Skel ═════════════════════════ */
const Skel = () => (
  <div className="px-5 pt-6">
    <div className="aspect-[16/10] w-full animate-pulse rounded-3xl bg-black/[0.05]" />
    <div className="mt-6 h-8 w-3/5 animate-pulse rounded-full bg-black/[0.05]" />
    <div className="mt-3 h-4 w-1/3 animate-pulse rounded-full bg-black/[0.04]" />
    <div className="mt-8 h-[6px] w-full animate-pulse rounded-full bg-black/[0.04]" />
  </div>
);

/* ═════════════════════════ MAIN ═════════════════════════ */
/* ═════════════════════════ MAIN ═════════════════════════
   Deux modes de chargement :
   - client (défaut)  → endpoints /api/client/* (session client)
   - preview (agence) → endpoints /api/dossiers/{id}  (visualisation en interne)

   La production utilise TOUJOURS le mode client. Le mode preview reste
   exposé sur `/app/espace-client-v3/:id` pour la recette agence. */
export default function EspaceClientV3({ mode = "client" }) {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agency, setAgency] = useState(null);
  const [settleLoading, setSettleLoading] = useState(false);

  const isClientMode = mode === "client";

  const loadDossier = async () => {
    if (isClientMode) {
      // Route production : session cookie → dossier scoped par le backend.
      // Si `routeId` est présent (compte multi-dossiers), on passe par
      // /client/dossier/{id} qui vérifie côté serveur l'appartenance au compte
      // (403 si tentative d'accès à un dossier tiers).
      const d = routeId
        ? await api.clientDossierById(routeId)
        : await api.clientDossier();
      setDossier(d);
      return d;
    }
    // Preview agence — nécessite ProtectedRoute agence.
    const d = await api.getDossier(routeId);
    setDossier(d);
    return d;
  };

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        let id = routeId;
        // Preview agence : si aucun id, prend un dossier démo
        if (!isClientMode && !id) {
          const list = await api.listDossiers();
          const items = Array.isArray(list) ? list : (list?.items || []);
          const target = items.find((x) => x.vehicle_image && !x.is_finished && x.progress >= 40) || items[0];
          if (target?.id) {
            if (!cancel) navigate(`/app/espace-client-v3/${target.id}`, { replace: true });
            return;
          }
        }
        const d = await loadDossier();
        if (cancel) return;

        // Agence courante :
        // - client mode → renseignée dans `d.agency` (sanitize_for_client)
        // - preview agence → via api.getAgency()
        if (isClientMode) {
          setAgency(d?.agency || null);
        } else {
          try {
            const ag = await api.getAgency();
            if (!cancel) setAgency(ag);
          } catch { /* preview only */ }
        }
      } catch (e) {
        if (cancel) return;
        // Isolation cross-dossier / expiration : renvoie vers login
        if (isClientMode && (e?.status === 401 || e?.status === 403 || e?.status === 404)) {
          navigate("/client/login", { replace: true });
          return;
        }
        setError(e?.message || "Erreur de chargement");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [routeId, isClientMode]);

  // Toggle "règlement en agence" — utilise l'endpoint client en mode client.
  const handleSettleInAgency = async (value) => {
    if (settleLoading) return;
    setSettleLoading(true);
    try {
      if (isClientMode) {
        await api.clientSettlePaymentInAgency(value);
      } else if (dossier?.id) {
        await api.settlePaymentInAgency(dossier.id, value);
      }
      await loadDossier();
    } catch (e) {
      console.error("settle-in-agency failed", e);
    } finally {
      setSettleLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen text-black"
      style={{
        background: "radial-gradient(800px 500px at 100% 0%, rgba(200,255,47,0.05) 0%, rgba(200,255,47,0) 45%), linear-gradient(180deg, #FAFAF7 0%, #F5F4EF 100%)",
      }}
      data-testid="client-v3-page"
    >
      <ClientHeader agency={agency} />

      {loading && (
        <div className="mx-auto w-full max-w-[560px] lg:max-w-[1440px] lg:px-10">
          <Skel />
        </div>
      )}
      {error && !loading && (
        <div className="mx-auto w-full max-w-[560px] lg:max-w-[720px]">
          <div className="mx-5 mt-10 rounded-2xl bg-white/60 p-6 text-center text-[13.5px] text-black/50"
               style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)" }}>
            {error}
          </div>
        </div>
      )}

      {/* ─────── MOBILE / TABLETTE (< lg) : layout single column strictement inchangé ─────── */}
      {!loading && dossier && (
        <div className="mx-auto w-full max-w-[560px] lg:hidden">
          <ClientHero        dossier={dossier} />
          <ClientProgress    dossier={dossier} />
          <ClientNextAction  dossier={dossier} onSettleInAgency={handleSettleInAgency} settleLoading={settleLoading} />
          <ClientTimeline    dossier={dossier} />
          <ClientDocuments   dossier={dossier} />
          <ClientMessages    dossier={dossier} onSent={loadDossier} mode={isClientMode ? "client" : "agency-preview"} />
          <ClientFooter      agency={agency} />
        </div>
      )}

      {/* ─────── DESKTOP (≥ lg) : hero pleine largeur + contenu centré 720 ─────── */}
      {!loading && dossier && (
        <div className="hidden lg:block">
          <ClientHeroDesktop dossier={dossier} />
          <div className="mx-auto max-w-[720px] pb-12">
            <ClientNextAction  dossier={dossier} onSettleInAgency={handleSettleInAgency} settleLoading={settleLoading} />
            <ClientTimeline    dossier={dossier} />
            <ClientDocuments   dossier={dossier} />
            <ClientMessages    dossier={dossier} onSent={loadDossier} mode={isClientMode ? "client" : "agency-preview"} />
            <ClientFooter      agency={agency} />
          </div>
        </div>
      )}
    </div>
  );
}
